// Help guides (Jill 2026-11-03): step-by-step directions with a picture of
// each screen, one guide per role, on /help. "Screenshots and your
// instructions will be a LOT more beneficial than a 5 minute video."
//
// Pictures are JPEGs in public/help/<guide>/, taken in the demo sandbox
// (made-up people). They load only when a topic is opened and are never in
// the offline cache (vite.config.js leaves help/** out of it). tests/helpGuides.test.mjs checks that every picture named here is
// in public/help and every picture there is named here.
//
// Who sees a guide (mirrors the Help Videos rule): trainees see the trainee
// guide; pilots (mentors, auditors, organizers and training pilots are all
// pilots) see every guide; system admins see everything. The office and the
// CLAA agent see none.

import { PILOT_GUIDES } from "./helpGuidesPilot.js";

// The training guides. Pilots see the pilot guides first (helpGuidesPilot.js),
// then these; trainees see the trainee guide only.
export const TRAINING_GUIDES = [
  {
    key: "trainee",
    title: "Trainee guide",
    about: "Requesting rides, making Training Records, getting them graded, and keeping your trackers.",
    topics: [
      {
        key: "cruise-ride",
        title: "Request a cruise ride",
        intro: "Find a ship with a training pilot aboard and ask the vessel agent (CLAA) for a ride.",
        steps: [
          { img: "cruise-ride-01.jpg", text: "Open Training Opportunities. A green day has a training pilot aboard. Tap the day you want to board." },
          { img: "cruise-ride-02.jpg", text: "The card shows who is aboard, the seats still open on each leg, and any trainee already riding that day. Tap Request a ride." },
          { img: "cruise-ride-03.jpg", text: "Check the board and off dates and places; the itinerary under them shows where the ship is each day. Pick your phase and the ride type: an overnight ride needs a room, a day ride is pilot station to dock or dock to pilot station." },
          { img: "cruise-ride-04.jpg", text: "Say what you are hoping to work on. About you goes to the pilots aboard and is saved for your next request. Tap Submit Request." },
          { img: "cruise-ride-05.jpg", text: "The ride shows under My Requests as REQUESTED. The CLAA forwards it to the ship and records the answer, and you get an email either way." },
          { img: "cruise-ride-06.jpg", text: "Tap the request for what you can do while you wait: Edit or Cancel it, email yourself an introduction to forward to the pilots aboard, or Draft a Plan B in case the ship says no." },
        ],
      },
      {
        key: "plan-b",
        title: "Set up a Plan B",
        intro: "A backup ride that goes to the CLAA on its own if your first ride gets No room or Never heard back.",
        steps: [
          { img: "plan-b-01.jpg", text: "On Training Opportunities, tap your request under My Requests, then tap Draft a Plan B." },
          { img: "plan-b-02.jpg", text: "A green bar says you are setting up a Plan B. It stays hidden from the CLAA until your first ride gets No room or Never heard back." },
          { img: "plan-b-03.jpg", text: "Tap a green day for the backup, then Request a ride. It has to board at least 36 hours after your first ride boards." },
          { img: "plan-b-04.jpg", text: "Fill in both planned boarding times, yours on the first ride and on this one, so the portal can check the 36 hours. Pick the places and ride type, then tap Save Plan B." },
          { img: "plan-b-05.jpg", text: "The backup sits beside your first ride as PLAN B (WAITING). If the first ride gets No room or Never heard back, the Plan B goes into the CLAA's queue and you get an email. You can cancel it any time from My Requests." },
        ],
      },
      {
        key: "cargo-ride",
        title: "Request a cargo ride",
        intro: "Cargo ships are posted by the Training Chair. Seats follow the rotation lists.",
        steps: [
          { img: "cargo-ride-01.jpg", text: "Tap More, then Cargo Training Requests. A dot means a ship is open for requests." },
          { img: "cargo-ride-02.jpg", text: "Each open ship shows its legs (inbound, outbound) and when requests close. On the maneuvering list, answer every ship: Request the work (training), Request assessment, or Pass on this one. A pass counts as a deferral." },
          { img: "cargo-ride-03.jpg", text: "Observer and tug seats are open to every trainee. Tap Request observer seat or Request tug seat on the leg you want." },
          { img: "cargo-ride-04.jpg", text: "Your name shows on each leg with where you stand, for example SELECTED or 1ST IN LINE. Withdraw takes you off; Travel lets you add your travel plans. When the Chair closes the request window, a draft Training Record for each seat you got appears on My Training." },
        ],
      },
      {
        key: "create-records",
        title: "Make Training Records for a ride",
        intro: "Every approved ride has its own box on My Training. Make its records before the ride, fill in the trip details, and keep them as drafts until the work is done.",
        steps: [
          { img: "create-records-01.jpg", text: "Open My Training. On the ride's box, tap Create Training Records." },
          { img: "create-records-02.jpg", text: "Each day of the ride shows the port from the ship's schedule. Tick Docking or Undocking for what you plan to do, or pick a transit from Add a transit." },
          { img: "create-records-03.jpg", text: "Tap Create. Each tick becomes one draft record, already dated and tagged to the right requirement." },
          { img: "create-records-04.jpg", text: "The drafts show in the ride's box as DRAFT NOT YET STARTED. Tap one to fill it in." },
          { img: "create-records-05.jpg", text: "Under How will this TR be graded, tap In the portal for a training pilot who grades on the portal, or On a printed TR for one who is not on the portal." },
          { img: "create-records-06.jpg", text: "The ship, date and tonnage are filled in. Answer Subject to Act, Night, Tug and Ice; each is Yes or No, never left blank." },
          { img: "create-records-07.jpg", text: "Check what it counts toward. The ticked requirement shows at the top; add another or remove one if needed. Simulated is asked only for a requirement that limits simulated maneuvers, and Location detail asks which port only where a limit counts per port." },
          { img: "create-records-08.jpg", text: "Tap Save Draft." },
          { img: "create-records-09.jpg", text: "The record waits in the ride's box as STARTED BUT NOT YET SENT TO TRAINING PILOT until you do the work. Do the same for each draft." },
        ],
        after: "To add more records later, tap Create Training Records on the same box again.",
      },
      {
        key: "print",
        title: "A training pilot not on the portal: print the TR",
        intro: "Print it before you board. The training pilot grades and signs the printed TR.",
        steps: [
          { img: "print-01.jpg", text: "Open the draft (On a printed TR is picked) and tap Print TR." },
          { img: "print-02.jpg", text: "The form has automatically filled in your trip details. Tap Download, then print or email it to the training pilot to grade and sign." },
          { img: "print-03.jpg", text: "To print every draft for a ride at once, tap Print TRs for this ride on the ride's box." },
        ],
      },
      {
        key: "send-portal",
        title: "After the work: send a TR to a training pilot in the portal",
        intro: "For a training pilot who grades on the portal.",
        steps: [
          { img: "send-portal-01.jpg", text: "Open the draft from the ride's box. Check the trip details still match what you did, and change any that did not go as planned." },
          { img: "send-portal-02.jpg", text: "Sign in the signature box, then pick the training pilot under Send for grading." },
          { img: "send-portal-03.jpg", text: "Tap Send to Training Pilot. If anything is still missing, the list under the buttons says what. It goes in the training pilot's TR Grading Queue, and their 7am daily email lists it." },
          { img: "send-portal-04.jpg", text: "The row now reads SENT TO (the pilot). WAITING FOR GRADE." },
          { img: "send-portal-05.jpg", text: "Once graded it reads NEW and GRADED BY (the pilot). If the training pilot marked Yes, it counts on Progress right away; your reviewer signs it afterward (your mentor, or the Training Chair if you have none; for a licensed deputy, the Deputy Organizer)." },
        ],
      },
      {
        key: "see-grade",
        title: "See your grade",
        intro: "No email comes when a record is graded or signed. A red dot on My Training and NEW on the record tell you it has landed.",
        steps: [
          { img: "see-grade-01.jpg", text: "Tap the record that reads NEW and GRADED BY. Under Grading, the grade the training pilot gave each line is filled in." },
          { img: "see-grade-02.jpg", text: "Below the grades are the training pilot's answers, including whether it counts toward licensure or upgrade, and their comments." },
        ],
      },
      {
        key: "upload",
        title: "Add signed uploaded TR to tracker",
        intro: "When the signed TR comes back, upload it onto the same record. If the training pilot marked Yes, it counts as soon as you add it.",
        steps: [
          { img: "upload-01.jpg", text: "Open the same record and tap Upload Signed TR." },
          { img: "upload-02.jpg", text: "Pick how: Take a photo (one page per photo; tap Add another page for the next), Choose a scan (a PDF you scanned in Files, Dropbox or PDF Expert), or Choose photos already on the device. Every page goes into one PDF." },
          { img: "upload-03.jpg", text: "Pick who signed the TR, then copy the training pilot's Yes or No on whether it counts." },
          { img: "upload-04.jpg", text: "Sign in the signature box and tap Add to Tracker. The pilot who signed is not emailed; it goes on your reviewer's list to check against your upload." },
          { img: "upload-05.jpg", text: "The row reads GRADED BY (the pilot). WAITING FOR (your reviewer) TO SIGN." },
          { img: "upload-06.jpg", text: "On Progress it counts now if the pilot marked Yes, marked not yet checked until your reviewer signs." },
        ],
        after: "Made a mistake on a TR you added? Open it and tap Fix details before your reviewer checks it.",
      },
      {
        key: "no-ride",
        title: "A trip with no ride in the portal",
        intro: "For work you did on a ship you never requested a ride on.",
        steps: [
          { img: "no-ride-01.jpg", text: "On My Training, tap New Training Record at the top." },
          { img: "no-ride-02.jpg", text: "Fill in the date and the vessel (picking a ship fills in its tonnage), then tick what it counts toward and finish it the same way as any other record." },
        ],
      },
      {
        key: "progress",
        title: "Read your progress",
        intro: "The Progress tab on My Training counts every record that counts.",
        steps: [
          { img: "progress-01.jpg", text: "Each group shows done out of required; tap a group to see each requirement. A number in italics counts but has a record your mentor or auditor has not signed yet." },
          { img: "progress-02.jpg", text: "By training pilot shows how many of your dockings and undockings each pilot supervised. The state caps one pilot at 40 percent, and the red warning says when to spread the next ones out." },
        ],
      },
      {
        key: "phase",
        title: "Ask to move to the next phase",
        steps: [
          { img: "phase-01.jpg", text: "On Progress, under Program phase, tap Request phase advancement." },
          { img: "phase-02.jpg", text: "Pick the track and the phase to move to, add a note if you like, and tap Send request. It goes to the Training Chair; your mentor sees it and can comment." },
          { img: "phase-03.jpg", text: "The request waits under Program phase until the Chair decides. You can Withdraw it before then." },
        ],
      },
      {
        key: "federal",
        title: "Keep your federal pilotage tracker",
        intro: "Round trips per route toward your federal first-class pilotage, on the Federal Trips tab of My Training.",
        steps: [
          { img: "federal-01.jpg", text: "Open My Training and tap Federal Trips. If you hold a federal pilotage endorsement, tick the box: every route drops to the lower USCG count." },
          { img: "federal-02.jpg", text: "Upload your MMC page if you like, and set your federal application date: the USCG wants one round trip on each route in the 6 months before you apply, and each route is checked against it." },
          { img: "federal-03.jpg", text: "Each route has a row of squares, one per round trip. Tap the next empty square." },
          { img: "federal-04.jpg", text: "Set the date, tick Night if it was in darkness, add the vessel if you like (its tonnage fills in), and tap Log NW leg or Log SE leg. Tap the square again for the other leg." },
          { img: "federal-05.jpg", text: "The row counts NW and SE legs, round trips done, night trips against what is required, and what is still needed. Tap a filled square to fix or remove a leg." },
          { img: "federal-06.jpg", text: "Pilot Lookup, at the bottom, has the license numbers you copy onto the Federal Pilotage Observer sheet." },
        ],
      },
      {
        key: "forms",
        title: "Download your forms for your training log",
        steps: [
          { img: "forms-01.jpg", text: "At the bottom of My Training, under My forms by quarter, pick the quarter and tap Download my forms. You get every graded form and every uploaded TR for that quarter in one file." },
        ],
      },
    ],
  },
  {
    key: "tp",
    title: "Training pilot guide",
    about: "Grading a Training Record a trainee sent you in the portal.",
    topics: [
      {
        key: "grade",
        title: "Grade and sign a Training Record",
        intro: "A record a trainee sends you waits in your TR Grading Queue. Each morning at 7 you get one email listing any new ones.",
        steps: [
          { img: "grade-01.jpg", text: "Tap More, then TR Grading Queue. The number is how many are waiting for you." },
          { img: "grade-02.jpg", text: "Records waiting for your grade are at the top, with how many days they have waited. Tap one." },
          { img: "grade-03.jpg", text: "Review the trainee filled in form for correctness." },
          { img: "grade-04.jpg", text: "Grade each line against the next licensed level. Tap Guidance on any line for what each grade means." },
          { img: "grade-05.jpg", text: "Answer the two questions. Should this movement count is the one that decides whether it counts. Write a comment; one is required for any D or F and any No." },
          { img: "grade-06.jpg", text: "Sign in your box and tap Sign & Return to Trainee." },
          { img: "grade-07.jpg", text: "It moves to Recently graded, waiting for the trainee's reviewer to sign: the mentor, or for a licensed deputy the Deputy Organizer." },
        ],
      },
    ],
  },
  {
    key: "mentor",
    title: "Mentor guide",
    about: "Signing your trainees' graded records, in the portal and on printed TRs.",
    topics: [
      {
        key: "review",
        title: "Sign a record graded in the portal",
        steps: [
          { img: "review-01.jpg", text: "Tap More, then Mentor Review. The number is how many are waiting for you." },
          { img: "review-02.jpg", text: "Each of your trainees shows how many records are waiting for you to sign. Tap Open records." },
          { img: "review-03.jpg", text: "On the trainee's records, tap a row that reads WAITING FOR YOU TO SIGN." },
          { img: "review-04.jpg", text: "Review the grades, the answers and the training pilot's comments. Ensure all required information, box checks and signatures are present." },
          { img: "review-05.jpg", text: "Sign in the Reviewer box and tap Sign Review." },
          { img: "review-06.jpg", text: "Back on Mentor Review, the count goes down." },
        ],
      },
      {
        key: "printed",
        title: "Check and sign a printed TR",
        intro: "A printed TR the training pilot marked Yes counts as soon as the trainee adds it. Your check against the upload is what confirms it.",
        steps: [
          { img: "printed-01.jpg", text: "Open the trainee's records and tap a printed TR that reads WAITING FOR YOU TO SIGN." },
          { img: "printed-02.jpg", text: "Tap View uploaded TR. It opens; check every grade and the Yes or No against it. Typing the grades into the portal is optional." },
          { img: "printed-03.jpg", text: "Tick I compared these grades and the Yes/No to the uploaded TR, and answer whether the signed TR shows any F grade. An F puts the record on the Training Chair's Did not count list." },
          { img: "printed-04.jpg", text: "Sign in the Reviewer box and tap Sign Review. If the trainee copied something wrong, tap Correct grades first." },
        ],
      },
    ],
  },
  {
    key: "auditor",
    title: "Auditor guide",
    about: "Milestone audits of your assigned trainees, and the records flagged for you to check.",
    topics: [
      {
        key: "audit",
        title: "Record a milestone audit",
        steps: [
          { img: "audit-01.jpg", text: "Tap More, then Committee Review." },
          { img: "audit-02.jpg", text: "Audits due lists your trainees whose audit is overdue (red) or due within 14 days (amber). An audit comes due on the schedule the Training Chair sets." },
          { img: "audit-03.jpg", text: "Under Milestone audits, tap Open records on the trainee. Look at Progress for counts, flags and the program clock, and Records for anything not yet checked." },
          { img: "audit-04.jpg", text: "Come back, pick Records verified or Needs review, write a short note of what you checked, and tap Save. Needs review tells the Training Chair." },
          { img: "audit-05.jpg", text: "The trainee's card shows the outcome and date, and the trainee leaves Audits due until the next one comes due." },
        ],
      },
      {
        key: "check",
        title: "Records to check",
        intro: "Some records count but have something the portal cannot settle on its own: gross tons missing, or a location that names no port on the station list, so a per-port limit cannot be checked. A record graded before the portal asked Simulated shows here too. They show only to the trainee's assigned auditor.",
        steps: [
          { img: "check-01.jpg", text: "On the trainee's card, tap the line that says how many there are to check. Each line names the record and what to check; tap it to open the record. Settle it with the trainee or the training pilot, and note it in your audit." },
        ],
      },
    ],
  },
  {
    key: "organizer",
    title: "Organizer guide",
    about: "The Trainee Organizer's and Deputy Organizer's final passes, and the Deputy Organizer's record reviews.",
    topics: [
      {
        key: "final",
        title: "Trainee final pass",
        intro: "Your stamp per trainee: recommend advancement or hold. The Training Chair sees it beside every advancement request.",
        steps: [
          { img: "final-01.jpg", text: "Tap More, then Committee Review." },
          { img: "final-02.jpg", text: "Trainee final pass lists every trainee. Tap Open records to look at a trainee's records and progress." },
          { img: "final-03.jpg", text: "Pick Recommend advancement or Hold, write a short note, and tap Save." },
          { img: "final-04.jpg", text: "The card shows your stamp and its date." },
        ],
      },
      {
        key: "deputy",
        title: "Deputy Organizer: deputy records and final pass",
        intro: "Licensed deputies have no mentor or auditor, so the Deputy Organizer signs their records and gives the final pass.",
        steps: [
          { img: "deputy-01.jpg", text: "Tap More, then Deputy Review (Mentor Review if you are also a mentor). Each deputy shows how many records are waiting for you to sign. Tap Records." },
          { img: "deputy-02.jpg", text: "Tap a row that reads WAITING FOR YOU TO SIGN." },
          { img: "deputy-03.jpg", text: "Read it, sign in the Reviewer box, and tap Sign Review. For a printed TR, check it against the upload first, the same way a mentor does." },
          { img: "deputy-04.jpg", text: "On Committee Review, Deputy upgrade verification lists each deputy with the final pass due date. Pick Recommend advancement or Hold, write a note, and tap Save." },
        ],
      },
    ],
  },
];

export const HELP_GUIDES = [...PILOT_GUIDES, ...TRAINING_GUIDES];


export const imgSrc = (guide, img) => `/help/${guide}/${img}`;

// The guides a login may open.
export function helpGuidesFor(roles = []) {
  const r = (roles || []).map((x) => String(x).toLowerCase());
  if (r.includes("system_admin") || r.includes("pilot")) return HELP_GUIDES;
  if (r.includes("trainee")) return HELP_GUIDES.filter((g) => g.key === "trainee");
  return [];
}
